import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Mail, Users, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BulkEmailModal = ({ 
  isOpen, 
  onClose, 
  onSend, 
  selectedUsers = [], 
  users = [],
  loading = false 
}) => {
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingProgress, setSendingProgress] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [sendingStatus, setSendingStatus] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSubject('');
      setEmailBody('');
      setSendingProgress(0);
      setIsSending(false);
      setSendingStatus('');
    }
  }, [isOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleSendEmail = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!emailBody.trim()) {
      toast.error('Please enter email content');
      return;
    }

    if (subscribedUsers.length === 0) {
      toast.error('No subscribed users selected. Only users subscribed to emails can receive messages.');
      return;
    }

    setIsSending(true);
    setSendingProgress(0);
    setSendingStatus('Preparing to send emails...');

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setSendingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      setSendingStatus('Sending emails to users...');
      
      await onSend(subject, emailBody, subscribedUsers);
      
      clearInterval(progressInterval);
      setSendingProgress(100);
      setSendingStatus('Emails sent successfully!');
      
      // Wait a moment to show completion
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('Error sending bulk email:', err);
      setSendingStatus('Failed to send emails');
      toast.error(`Failed to send email: ${err.message}`);
      setIsSending(false);
      setSendingProgress(0);
    }
  };

  // Get user details for display
  const getSelectedUserDetails = () => {
    return selectedUsers.map(userId => {
      const user = users.find(u => u.key === userId);
      return user ? {
        id: userId,
        name: user.displayNameOrEmail,
        email: user.username || user.email || 'No email',
        subscribed: !user.emailUnsubscribed
      } : { id: userId, name: 'Unknown User', email: 'No email', subscribed: false };
    });
  };

  // Filter out unsubscribed users
  const getSubscribedUsers = () => {
    return selectedUsers.filter(userId => {
      const user = users.find(u => u.key === userId);
      return user && !user.emailUnsubscribed;
    });
  };

  const subscribedUsers = getSubscribedUsers();
  const unsubscribedCount = selectedUsers.length - subscribedUsers.length;

  if (!isOpen) return null;

  const selectedUserDetails = getSelectedUserDetails();

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-hidden touch-none pointer-events-auto">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-3xl overflow-hidden overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-blue-500" />
            <span className="font-bold text-xl text-gray-900">Send Bulk Email</span>
            <div className="flex gap-2">
              <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded-full">
                {subscribedUsers.length} subscribed
              </span>
              {unsubscribedCount > 0 && (
                <span className="text-xs text-gray-500 bg-orange-100 px-2 py-1 rounded-full">
                  {unsubscribedCount} unsubscribed
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            disabled={isSending}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Indicator */}
        {isSending && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-3 mb-2">
              {sendingProgress === 100 ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              )}
              <span className="text-sm font-medium text-blue-700">
                {sendingStatus}
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${sendingProgress}%` }}
              ></div>
            </div>
            <div className="text-xs text-blue-600 mt-1">
              {sendingProgress}% complete
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-6 bg-gray-50">
          {/* Recipients List */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              Recipients ({subscribedUsers.length} subscribed, {unsubscribedCount} unsubscribed):
            </label>
            
            {unsubscribedCount > 0 && (
              <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-800">
                    <strong>Note:</strong> {unsubscribedCount} selected user{unsubscribedCount > 1 ? 's are' : ' is'} unsubscribed from emails and will not receive this message.
                  </span>
                </div>
              </div>
            )}
            
            <div className="max-h-24 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white">
              {selectedUserDetails.map((user, index) => (
                <div key={user.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${user.subscribed ? 'text-gray-900' : 'text-gray-400'}`}>
                      {user.name}
                    </span>
                    <span className={`text-xs ${user.subscribed ? 'text-gray-500' : 'text-gray-400'}`}>
                      ({user.email})
                    </span>
                    {!user.subscribed && (
                      <span className="text-xs text-orange-600 bg-orange-100 px-1 py-0.5 rounded">
                        Unsubscribed
                      </span>
                    )}
                  </div>
                  {index < selectedUserDetails.length - 1 && (
                    <div className="text-gray-300">•</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Subject Field */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              Subject:
            </label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSending}
              maxLength={200}
            />
          </div>

          {/* Email Body */}
          <div className="mb-4">
            <label className="block mb-2 font-medium text-gray-700 text-base">
              Email Body:
            </label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              placeholder="Write your email content here..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              disabled={isSending}
              rows={8}
              maxLength={5000}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            className="px-4 py-2 rounded-lg text-base text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white text-base font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60"
            onClick={handleSendEmail}
            disabled={isSending}
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send to {subscribedUsers.length} Users
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkEmailModal; 